<template>
  <BaseModal :modalTitle="$t('W: Vælg analyser')">
    <div class="flex max-h-[60vh] flex-col gap-6 overflow-y-auto px-6 pb-2">
      <div
        v-for="group in analysisGroups"
        :key="group.id">
        <p class="mb-2 text-sm font-medium text-text-secondary">
          {{ group.name }}
        </p>
        <UiInputCheckboxGroupVee
          :name="`tatAnalysisIDs-${group.id}`"
          :options="group.options"
          :modelValue="localSelectedIDs"
          @update:model-value="localSelectedIDs = $event" />
      </div>

      <p
        v-if="analysisGroups.length === 0"
        class="text-sm text-text-muted">
        {{ $t('area.pricing-segment.message.no-compatible-analyses') }}
      </p>
    </div>

    <template #buttons>
      <UiBaseButton
        :text="$t('action.cancel')"
        theme="neutral"
        @button-click="closeTop" />
      <UiBaseButton
        :text="$t('action.save')"
        @button-click="onConfirmClick" />
    </template>
  </BaseModal>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useSampleAreaStore } from '@/stores/sampleAreaStore'
import { useAppStore } from '@/stores/appStore'
import { useModalStore } from '@/stores/modalStore'
import { mapActions, mapState } from 'pinia'
import {
  groupAnalysesBySampleArea,
  type IAnalysisPickerGroup,
} from '@/globals/typescript/utils/analysisPickerGroups'

/**
 * Picks which analyses one TAT price line covers, on the pricing segment and
 * the customer pricing pages alike.
 *
 * Three visibility rules, all decided by the caller passing ID sets — this
 * modal deliberately knows nothing about pricing:
 *  - **incompatible with the TAT → hidden, unless this line already covers
 *    it.** Offering one invites creating exactly the stale pairs
 *    `isIncompatibleExplicitPrice` exists to flag. But hiding one that is
 *    *already* selected made the flagged price unfixable: it could not be
 *    unticked, and confirming wrote the hidden ID straight back, so the only
 *    escape was deleting the whole price line. It is therefore shown while
 *    selected, and locks once removed — off, but not back on.
 *  - **claimed by another price line of the same TAT → shown but locked.**
 *    Two lines naming the same analysis cannot be represented: the persisted
 *    shape resolves to whichever matches first, so the second silently does
 *    nothing. Shown rather than hidden so it is obvious *why* it is
 *    unavailable.
 *  - **deactivated for this customer → shown and selectable, but labelled.**
 *    Pricing a TAT for a disabled analysis is pointless rather than harmful,
 *    and silently hiding it would be more confusing than saying so.
 *
 * It also surfaces what the segment already charges for this TAT x analysis
 * pair, so the number being typed can be compared against what it replaces
 * without leaving the modal. That too arrives as data (`inheritedPrices`) — the
 * pricing segment page passes nothing, since it inherits from no one.
 */
export default {
  name: 'TatAnalysisPickerModal',

  props: {
    /** This price line's current selection. */
    selectedAnalysisIDs: {
      type: Array as PropType<string[]>,
      required: true,
    },
    /** Analyses the TAT can actually serve — anything else is hidden. */
    compatibleAnalysisIDs: {
      type: Array as PropType<string[]>,
      required: true,
    },
    /** Claimed by a sibling price line of the same TAT — shown, locked. */
    takenAnalysisIDs: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    /** Deactivated for this customer on the Analyser tab — labelled only. */
    deactivatedAnalysisIDs: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    /**
     * What the segment charges for this TAT and each analysis. Empty on the
     * pricing segment page, which inherits from nothing — so no inherited
     * price is shown there rather than a misleading zero.
     */
    inheritedPrices: {
      type: Object as PropType<Record<string, number | null>>,
      default: () => ({}),
    },
    currencySymbol: {
      type: String,
      default: '',
    },
    onConfirm: {
      type: Function as PropType<(analysisIDs: string[]) => void>,
      required: true,
    },
  },

  data() {
    return {
      localSelectedIDs: [...this.selectedAnalysisIDs] as string[],
    }
  },

  computed: {
    ...mapState(useAnalysisStore, ['activeAnalyses']),
    ...mapState(useSampleAreaStore, ['allSampleAreas']),
    ...mapState(useAppStore, ['currentLocale']),

    compatibleIDSet(): Set<string> {
      return new Set(this.compatibleAnalysisIDs)
    },

    takenIDSet(): Set<string> {
      return new Set(this.takenAnalysisIDs)
    },

    deactivatedIDSet(): Set<string> {
      return new Set(this.deactivatedAnalysisIDs)
    },

    analysisGroups(): IAnalysisPickerGroup[] {
      return groupAnalysesBySampleArea({
        analyses: this.activeAnalyses,
        sampleAreas: this.allSampleAreas,
        locale: this.currentLocale,
        ungroupedLabel: this.$t('W: Uden prøveområde'),
        toOption: (analysis) => {
          const isCompatible = this.compatibleIDSet.has(analysis.id)
          const isStale =
            !isCompatible && this.selectedAnalysisIDs.includes(analysis.id)
          // Never offered, and gone once this line no longer covers it.
          if (!isCompatible && !isStale) return null
          // Unticking it removes it from the draft; from that point the option
          // locks rather than vanishing, so it is clear it cannot come back.
          const isRemoved = isStale && !this.localSelectedIDs.includes(analysis.id)

          const name = analysis.getName(this.currentLocale)
          const isTaken = this.takenIDSet.has(analysis.id)
          return {
            value: analysis.id,
            text: this.deactivatedIDSet.has(analysis.id)
              ? `${name} (${this.$t('area.pricing.deactivated-label')})`
              : name,
            description: isStale
              ? this.$t('W: Uforenelig med denne svartid — kan kun fjernes')
              : this.buildDescription(analysis.id, isTaken),
            isDisabled: isTaken || isRemoved,
            hideCheckWhenDisabled: isTaken,
          }
        },
      })
    },
  },

  methods: {
    ...mapActions(useModalStore, ['closeTop']),

    /**
     * Both facts worth knowing before ticking a box, joined rather than one
     * winning: that a sibling line already claims it, and what the segment
     * charges for this pair today.
     */
    buildDescription(analysisID: string, isTaken: boolean): string | undefined {
      const parts: string[] = []
      if (isTaken) parts.push(this.$t('W: Bruges i en anden prislinje'))

      // One label either way, so the two states read as the same fact with a
      // different value. Naming it the *svartid* price matters: "I segment:
      // 300 DKK" was ambiguous, since a reader could take 300 for the
      // analysis price rather than the price for this turnaround time.
      //
      // "Ikke sat" rather than "not in segment" — an analysis can be in the
      // segment and still have no price for this particular svartid (a walk-in
      // priced for only a few analyses is the common case), so saying it is
      // absent from the segment would be wrong.
      if (Object.keys(this.inheritedPrices).length > 0) {
        const inherited = this.inheritedPrices[analysisID]
        parts.push(
          inherited === undefined || inherited === null
            ? this.$t('W: Segment svartid pris: Ikke sat')
            : this.$t('W: Segment svartid pris: {price} {currency}', {
                price: inherited,
                currency: this.currencySymbol,
              })
        )
      }

      return parts.length ? parts.join(' · ') : undefined
    },

    onConfirmClick(): void {
      this.onConfirm(this.localSelectedIDs)
      this.closeTop()
    },
  },
}
</script>
